import { IPoint } from "@formant/core/model/IPoint";
import { IQuaternion } from "@formant/core/model/IQuaternion";
import { IVector3 } from "@formant/core/model/IVector3";
import {
    rtcPointStreamType,
    rtcPoseStreamType,
    rtcPoseWithCovarianceStreamType,
    rtcTwistStreamType,
} from "@formant/core/model/rtcStreamTypes";

import { IRtcStreamMessage } from "../models/IRtcStreamMessage";

export function reifyZeroValues(message: IRtcStreamMessage): IRtcStreamMessage {
    switch (message.header.stream.streamType) {
        case rtcTwistStreamType: {
            const { twist } = message.payload;
            if (!twist) {
                throw Error(
                    "twist not in payload of RTC message with type twist",
                );
            }
            return {
                header: message.header,
                payload: {
                    twist: {
                        linear: {
                            x: 0.0,
                            y: 0.0,
                            z: 0.0,
                            ...(twist.linear as Partial<IVector3>),
                        },
                        angular: {
                            x: 0.0,
                            y: 0.0,
                            z: 0.0,
                            ...(twist.angular as Partial<IQuaternion>),
                        },
                    },
                },
            };
        }
        case rtcPoseStreamType: {
            const { pose } = message.payload;
            if (!pose) {
                throw Error(
                    "pose not in payload of RTC message with type pose",
                );
            }
            return {
                header: message.header,
                payload: {
                    pose: {
                        translation: {
                            x: 0.0,
                            y: 0.0,
                            z: 0.0,
                            ...(pose.translation as Partial<IVector3>),
                        },
                        rotation: {
                            x: 0.0,
                            y: 0.0,
                            z: 0.0,
                            w: 0.0,
                            ...(pose.rotation as Partial<IQuaternion>),
                        },
                    },
                },
            };
        }
        case rtcPoseWithCovarianceStreamType: {
            const { poseWithCovariance } = message.payload;
            if (!poseWithCovariance) {
                throw Error(
                    "poseWithCovariance not in payload of RTC message with type pose-with-covariance",
                );
            }

            const covariance = new Array<number>(36).fill(0);

            poseWithCovariance.covariance.forEach((value, index) => {
                if (index >= 36) {
                    throw Error("covariance contains more than 36 elements");
                }
                covariance[index] = value;
            });
            return {
                header: message.header,
                payload: {
                    poseWithCovariance: {
                        pose: {
                            translation: {
                                x: 0.0,
                                y: 0.0,
                                z: 0.0,
                                ...(poseWithCovariance.pose
                                    .translation as Partial<IVector3>),
                            },
                            rotation: {
                                x: 0.0,
                                y: 0.0,
                                z: 0.0,
                                w: 0.0,
                                ...(poseWithCovariance.pose
                                    .rotation as Partial<IQuaternion>),
                            },
                        },
                        covariance,
                    },
                },
            };
        }
        case rtcPointStreamType: {
            const { point } = message.payload;
            if (!point) {
                throw Error(
                    `point not in payload of RTC message with type ${rtcPointStreamType}`,
                );
            }
            return {
                header: message.header,
                payload: {
                    point: {
                        x: 0,
                        y: 0,
                        z: 0,
                        ...(point as Partial<IPoint>),
                    },
                },
            };
        }
        default:
            return message;
    }
}
